import React, { useState, useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Eager load critical components (needed immediately)
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import FeaturesSection from "./components/FeaturesSection";
import Footer from "./components/Footer";
import AuthModal from "./components/AuthModal";
import SplashScreen from "./components/SplashScreen";

// Lazy load page components (loaded on demand)
const CourseLearningPage = lazy(() => import("./components/CourseLearningPage"));
const AdminLoginPage = lazy(() => import("./components/AdminLoginPage"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const ModeratorLoginPage = lazy(() => import("./components/InstructorLoginPage"));
const ModeratorDashboard = lazy(() => import("./components/InstructorDashboard"));
const VerifyEmailPage = lazy(() => import("./components/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./components/ForgotPasswordPage"));
const DashboardMulai = lazy(() => import("./components/DashboardMulai"));
const BelajarPage = lazy(() => import("./components/BelajarPage"));
const RefleksiPage = lazy(() => import("./components/RefleksiPage"));
const DialogPage = lazy(() => import("./components/DialogPage"));
const ProfilPage = lazy(() => import("./components/ProfilPage"));
const ModulePage = lazy(() => import("./components/ModulePage"));
const DonasiPage = lazy(() => import("./components/DonasiPage"));
const StaticPage = lazy(() => import("./components/StaticPage"));
const ArticlePage = lazy(() => import("./components/ArticlePage"));
const ArticleListPage = lazy(() => import("./components/ArticleListPage"));

// Loading fallback for lazy components
const LoadingFallback = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Memuat...</p>
    </div>
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Auth guard for protected routes
const RequireAuth = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/?login=true" replace />;
  return children;
};

// Role guard for admin/instructor routes
const RequireRole = ({ children, roles }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/?login=true" replace />;
  if (!roles.includes(user?.role)) return <Navigate to="/mulai" replace />;
  return children;
};

// 404 page
const NotFoundPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center px-4">
        <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-2">Halaman tidak ditemukan</p>
        <p className="text-gray-400 mb-6">Halaman yang Anda cari tidak tersedia.</p>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
        >
          Kembali ke Beranda
        </button>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [searchParams] = useSearchParams();
  const [authModal, setAuthModal] = useState({ isOpen: false, mode: 'login' });
  const { checkAuth } = useAuth();

  useEffect(() => {
    if (searchParams.get('login') === 'true') {
      setAuthModal({ isOpen: true, mode: 'login' });
    }
  }, [searchParams]);

  const openLogin = () => setAuthModal({ isOpen: true, mode: 'login' });
  const openRegister = () => setAuthModal({ isOpen: true, mode: 'register' });
  const closeAuth = () => {
    setAuthModal({ isOpen: false, mode: authModal.mode });
    checkAuth();
  };
  const switchAuthMode = () => setAuthModal({
    isOpen: true,
    mode: authModal.mode === 'login' ? 'register' : 'login'
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar onLoginClick={openLogin} onRegisterClick={openRegister} />
      <HeroSection onRegisterClick={openRegister} onLoginClick={openLogin} />
      <FeaturesSection onRegisterClick={openRegister} />
      <Footer onRegisterClick={openRegister} />

      {/* Modals */}
      <AuthModal
        isOpen={authModal.isOpen}
        onClose={closeAuth}
        mode={authModal.mode}
        onSwitchMode={switchAuthMode}
      />
    </div>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const isAdminOrModerator = window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/moderator');

  return (
    <ErrorBoundary>
      <div className="App">
        {showSplash && !isAdminOrModerator && <SplashScreen onComplete={() => setShowSplash(false)} />}
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ForgotPasswordPage />} />
                <Route path="/halaman/:slug" element={<StaticPage />} />
                <Route path="/artikel" element={<ArticleListPage />} />
                <Route path="/artikel/:slug" element={<ArticlePage />} />

                {/* Fitrah Learning Routes (protected) */}
                <Route path="/mulai" element={<RequireAuth><DashboardMulai /></RequireAuth>} />
                <Route path="/belajar" element={<RequireAuth><BelajarPage /></RequireAuth>} />
                <Route path="/belajar/:pathId" element={<RequireAuth><BelajarPage /></RequireAuth>} />
                <Route path="/refleksi" element={<RequireAuth><RefleksiPage /></RequireAuth>} />
                <Route path="/dialog" element={<RequireAuth><DialogPage /></RequireAuth>} />
                <Route path="/profil" element={<RequireAuth><ProfilPage /></RequireAuth>} />
                <Route path="/modul/:courseId" element={<RequireAuth><ModulePage /></RequireAuth>} />
                <Route path="/donasi" element={<RequireAuth><DonasiPage /></RequireAuth>} />

                {/* Legacy route redirect */}
                <Route path="/course/:courseId" element={<RequireAuth><CourseLearningPage /></RequireAuth>} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<RequireRole roles={['admin']}><AdminDashboard /></RequireRole>} />

                {/* Moderator Routes */}
                <Route path="/moderator" element={<ModeratorLoginPage />} />
                <Route path="/moderator/dashboard" element={<RequireRole roles={['admin', 'moderator']}><ModeratorDashboard /></RequireRole>} />

                {/* 404 catch-all */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;
